/**
 * HOTEL-SVC-1 · Hotel Services page
 *
 * Grid of toggleable service cards. Loads the catalog + current state from
 * the backend and persists edits with dependency auto-resolution.
 *
 * Mounted at:
 *   - /platform/hotels/:hotelId/services   (super-admin scope)
 *   - /hotel-settings/services             (current-hotel scope, hotel admin)
 */
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Utensils, Wine, Waves, PartyPopper, Coffee, Beer, Flower2, BellRing, ChefHat,
  Loader2, ShieldCheck,
} from "lucide-react";

const ICON_MAP = {
  Utensils, Wine, Waves, PartyPopper, Coffee, Beer, Flower2, BellRing, ChefHat,
};

/**
 * @param {object} props
 * @param {string} [props.hotelId]  – Explicit hotel to edit (super-admin usage). If omitted, uses X-Hocp-Hotel from the request interceptor (current hotel).
 * @param {string} [props.title]    – Section title override.
 */
export default function HotelServicesEditor({ hotelId: hotelIdProp, title, embedded }) {
  const params = useParams();
  const hotelId = hotelIdProp || params.hotelId || null;

  const [catalog, setCatalog] = useState([]);
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [source,  setSource]  = useState("");

  const readPath  = hotelId ? `/hotels/v1/${hotelId}/services` : `/hotels/v1/services`;
  // If a URL hotelId is present (Super Admin scope) we write to that hotel;
  // otherwise we fall back to the current-hotel PUT which resolves via the
  // `X-Hocp-Hotel` interceptor / cookie.
  const writePath = hotelId ? `/hotels/v1/${hotelId}/services` : `/hotels/v1/services`;

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/hotels/v1/services/catalog"),
        api.get(readPath),
      ]);
      setCatalog(c.data?.services || []);
      setServices(s.data?.services || {});
      setSource(s.data?.source || "");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (e?.response?.status === 400 && detail.includes("Hotel context")) {
        // Load the catalog only so the empty state can still render.
        try {
          const c = await api.get("/hotels/v1/services/catalog");
          setCatalog(c.data?.services || []);
        } catch { /* ignore */ }
        setServices({});
        setSource("no_hotel_context");
      } else {
        toast.error("Failed to load hotel services");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [hotelId, readPath]);

  const enabledCount = useMemo(
    () => Object.values(services).filter((v) => v?.enabled).length,
    [services]
  );

  const toggle = async (code) => {
    const next = {
      ...services,
      [code]: { enabled: !services?.[code]?.enabled },
    };
    setServices(next);
    // When embedded (e.g. inside the Create-Hotel wizard) we auto-persist on
    // every toggle so the parent's Finish button always reflects the true
    // saved state.
    if (embedded) {
      try {
        const payload = Object.fromEntries(
          catalog.map((s) => [s.code, { enabled: !!next?.[s.code]?.enabled }])
        );
        const r = await api.put(writePath, { services: payload });
        setServices(r.data?.services || {});
        window.dispatchEvent(new Event("hocp:services-changed"));
      } catch (e) {
        toast.error("Failed to update service");
      }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        catalog.map((s) => [s.code, { enabled: !!services?.[s.code]?.enabled }])
      );
      const r = await api.put(writePath, { services: payload });
      setServices(r.data?.services || {});
      window.dispatchEvent(new Event("hocp:services-changed"));
      const auto = Object.entries(r.data?.services || {})
        .filter(([, v]) => v?.auto_enabled_by)
        .map(([k]) => k);
      if (auto.length) {
        toast.success(
          `Services updated. Auto-enabled: ${auto.join(", ")} (required by their dependencies).`
        );
      } else {
        toast.success("Services updated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save services");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-white/60"
           data-testid="hotel-services-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading services…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hotel-services-editor">
      {!embedded && (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-hocp-gold" />
              {title || "Hotel Services"}
            </h2>
            <p className="text-sm text-white/60 mt-1 max-w-2xl">
              Toggle the optional services this hotel offers. Core modules
              (Front Desk, Reservations, Housekeeping, Maintenance, Finance) are
              always on. Turning a service off simply hides its module — no data
              is ever deleted.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-white/20 text-white/80" data-testid="services-enabled-count">
              {enabledCount} / {catalog.length} enabled
            </Badge>
            <Button onClick={save} disabled={saving}
                    data-testid="save-services-btn"
                    className="bg-hocp-gold text-hocp-navy hover:bg-hocp-gold/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        </div>
      )}

      {source === "default_all_enabled" && (
        <div className="rounded-lg border border-hocp-gold/30 bg-hocp-gold/10 p-3 text-sm text-hocp-gold"
             data-testid="services-default-banner">
          This hotel has never customised its service list. All services are
          enabled by default. Trim to only the services you actually offer for
          a cleaner staff sidebar.
        </div>
      )}

      {source === "no_hotel_context" && (
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 text-sm text-yellow-200"
             data-testid="services-no-hotel-banner">
          Please select a hotel from the switcher (top-left) before editing
          services. Super Admins can also open a specific hotel&apos;s services page
          from Platform → Hotels → [any hotel] → Services.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {catalog.map((s) => {
          const Icon = ICON_MAP[s.icon] || Utensils;
          const enabled = !!services?.[s.code]?.enabled;
          const auto = services?.[s.code]?.auto_enabled_by;
          const deps = s.depends_on || [];

          if (embedded) {
            // Modern premium card style for wizard flow.
            return (
              <button
                key={s.code}
                type="button"
                data-testid={`service-card-${s.code}`}
                onClick={() => toggle(s.code)}
                className={`group relative text-left overflow-hidden rounded-xl p-5
                            transition-all duration-200 outline-none
                            focus-visible:ring-2 focus-visible:ring-amber-400
                            ${
                              enabled
                                ? "bg-white border border-amber-300 ring-1 ring-amber-200 shadow-sm hover:shadow-md"
                                : "bg-white border border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md"
                            }`}
              >
                <div className="relative flex items-start justify-between gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors
                                   ${enabled
                                     ? "bg-amber-500 text-white"
                                     : "bg-slate-100 text-slate-400"}`}>
                    <Icon className="w-6 h-6" strokeWidth={1.75} />
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={() => toggle(s.code)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`service-toggle-${s.code}`}
                    className="data-[state=checked]:bg-amber-500"
                  />
                </div>

                <div className="relative mt-5">
                  <h3 className="text-[17px] font-semibold text-slate-900 tracking-tight">
                    {s.label}
                  </h3>
                  <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">
                    {s.description}
                  </p>
                </div>

                {(deps.length > 0 || auto) && (
                  <div className="relative mt-4 pt-3 border-t border-slate-100 flex items-center flex-wrap gap-2">
                    {deps.length > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 tracking-wide uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Requires {deps.join(" · ")}
                      </span>
                    )}
                    {auto && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                        Auto-enabled by {auto}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          }

          // Standalone dark-theme card (Hotel Settings page).
          return (
            <Card key={s.code}
                  data-testid={`service-card-${s.code}`}
                  className={`bg-white/[0.03] border-white/10 transition-all
                              ${enabled ? "ring-1 ring-hocp-gold/60" : ""}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                                     ${enabled ? "bg-hocp-gold/20 text-hocp-gold" : "bg-white/5 text-white/40"}`}>
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <CardTitle className="text-base text-white">{s.label}</CardTitle>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={() => toggle(s.code)}
                    data-testid={`service-toggle-${s.code}`}
                  />
                </div>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <p className="text-xs text-white/60 leading-relaxed">
                  {s.description}
                </p>
                {deps.length > 0 && (
                  <div className="text-[11px] text-white/45">
                    Requires: {deps.join(", ")}
                  </div>
                )}
                {auto && (
                  <Badge variant="outline" className="text-[10px] border-hocp-gold/50 text-hocp-gold">
                    auto-enabled by {auto}
                  </Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
